import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { FETCHING_STATUS } from '@/constants';
import { WrappedPortfolio } from '@/entities/WrappedPortfolio';
import { ItemStatus } from '@/types/redux';
import { TokenInfo } from '@/types/token';

import { IAssetsBalance } from '@/types/assets';
import { AppState } from '../store';
import {
  clickOpenSettledPairModal,
  setPortfolioAssetSelect,
  setPortfolioDepositOrWithdrawOperateToken,
  settle,
} from './actions';

export interface IPortfolioState {
  portfolioAssetSelected: IAssetsBalance | null;
  chainSettlingPortfolioModalState: {
    [chainID: number]: {
      [userAddr: string]: ItemStatus<WrappedPortfolio>;
    };
  };

  chainBalanceOperateToken: {
    [chainID: number]: {
      [userAddr: string]: TokenInfo;
    };
  };
}

const initialState: IPortfolioState = {
  portfolioAssetSelected: null,
  chainSettlingPortfolioModalState: {},
  chainBalanceOperateToken: {},
};

export const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setPortfolioAssetSelect, (state, action) => {
      state.portfolioAssetSelected = action.payload.assetSelected;
    });
    builder.addCase(clickOpenSettledPairModal.fulfilled, (state, action) => {
      const { arg } = action.meta;
      _.set(state.chainSettlingPortfolioModalState, [arg.chainId, arg.userAddr, 'item'], action.payload);
    });
    builder.addCase(settle.pending, (state, action) => {
      const { arg } = action.meta;
      _.set(state.chainSettlingPortfolioModalState, [arg.chainId, arg.userAddr, 'status'], FETCHING_STATUS.FETCHING);
    });
    builder.addCase(settle.fulfilled, (state, action) => {
      const { arg } = action.meta;
      _.set(state.chainSettlingPortfolioModalState, [arg.chainId, arg.userAddr, 'status'], FETCHING_STATUS.DONE);
      _.set(state.chainSettlingPortfolioModalState, [arg.chainId, arg.userAddr, 'item'], undefined);
    });
    builder.addCase(settle.rejected, (state, action) => {
      const { arg } = action.meta;
      _.set(state.chainSettlingPortfolioModalState, [arg.chainId, arg.userAddr, 'status'], FETCHING_STATUS.DONE);
    });
    builder.addCase(setPortfolioDepositOrWithdrawOperateToken, (state, action) => {
      _.set(
        state.chainBalanceOperateToken,
        [action.payload.chainId, action.payload.userAddr],
        action.payload.tokenInfo,
      );
    });
  },
});

export const selectSettlePortfolioState =
  (chainId: number | undefined, userAddr: string | undefined) =>
  (state: AppState): WrappedPortfolio | undefined =>
    _.get(state.portfolio.chainSettlingPortfolioModalState, [chainId || '', userAddr || '', 'item']);

export const selectSettleFetchingStatus =
  (chainId: number | undefined, userAddr: string | undefined) =>
  (state: AppState): FETCHING_STATUS =>
    _.get(
      state.portfolio.chainSettlingPortfolioModalState,
      [chainId || '', userAddr || '', 'status'],
      FETCHING_STATUS.INIT,
    );

export const selectOperateToken =
  (chainId: number | undefined, userAddr: string | undefined) =>
  (state: AppState): TokenInfo | undefined =>
    _.get(state.portfolio.chainBalanceOperateToken, [chainId || '', userAddr || '']);

export default portfolioSlice.reducer;
